import { useState } from 'react';

import {
  useNylasAuthUrlLazyQuery,
  useRevokeNylasIntegrationMutation,
} from '../../../../generated/graphql';

import { useAuth } from '../../../../contexts/AuthContext';
import { CalendarProvider } from '../../../../generated/graphql';
import {
  PROVIDER_STRING_GOOGLE,
  PROVIDER_STRING_MICROSOFT,
  isMicrosoftProvider,
} from '../../../lib/calendarProvider';
import Integration from '../../../components/Integration';
import PageContainer from '../../../components/Containers/PageContainer';
import { trackProviderEvent } from '../../../../lib/analytics';
import { ProviderAnalyticsEvent } from '../../../../lib/analytics';
import GoogleCalLogo from '../../../svgs/GoogleCalLogo';
import SignInWithGoogle from '../../../svgs/SignInWithGoogle';
import SignInWithMicrosoft from '../../../svgs/SignInWithMicrosoft';
import IconButton from '../../../components/IconButton';
import GoogleCalendarLogo from '../../../svgs/GoogleCalendarLogo';
import MicrosoftOutlookLogo from '../../../svgs/MicrosoftOutlookLogo';

const NylasIntegration: React.FC = () => {
  const [isIntegrationLoading, setIsIntegrationLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { authedProviderUser, refreshAuthedProviderUser } = useAuth();

  const [revokeNylasIntegrationMutation] = useRevokeNylasIntegrationMutation();

  const [getNylasAuthUrl] = useNylasAuthUrlLazyQuery();

  const redirectToNylasAuthUrl = (
    authUrl: string,
    provider: typeof PROVIDER_STRING_GOOGLE | typeof PROVIDER_STRING_MICROSOFT,
  ) => {
    // Add provider parameter to the URL for callback identification.
    // Reject malformed/relative URLs from the backend instead of navigating.
    let url: URL;
    try {
      url = new URL(authUrl);
    } catch {
      setErrorMessage(
        'Unable to start calendar connection. Please try again, or contact support if this continues.',
      );
      return;
    }
    url.searchParams.set('state', JSON.stringify({ provider }));
    window.location.href = url.toString();
  };

  const onPressConnectGoogle = async () => {
    trackProviderEvent(ProviderAnalyticsEvent.AddNylasIntegrationAttempted);
    setErrorMessage(null);

    const response = await getNylasAuthUrl({
      variables: { provider: CalendarProvider.Google },
    });
    if (response.data?.nylasAuthUrl) {
      redirectToNylasAuthUrl(
        response.data.nylasAuthUrl,
        PROVIDER_STRING_GOOGLE,
      );
    }
  };

  const onPressConnectMicrosoft = async () => {
    trackProviderEvent(ProviderAnalyticsEvent.AddNylasIntegrationAttempted);
    setErrorMessage(null);

    const response = await getNylasAuthUrl({
      variables: { provider: CalendarProvider.Microsoft },
    });
    if (response.data?.nylasAuthUrl) {
      redirectToNylasAuthUrl(
        response.data.nylasAuthUrl,
        PROVIDER_STRING_MICROSOFT,
      );
    }
  };

  const onPressDisconnectNylas = async () => {
    setIsIntegrationLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      await revokeNylasIntegrationMutation();
      await refreshAuthedProviderUser();
      setSuccessMessage(
        `Your calendar is now disconnected from Homecoming. If you'd like to reconnect your calendar, you can do so at any time.`,
      );
    } catch (error) {
      setErrorMessage((error as Error).message);
    } finally {
      setIsIntegrationLoading(false);
    }
  };

  return (
    <PageContainer containerClassName="min-h-[calc(100vh-200px)] flex text-neutral-125 items-center">
      <Integration
        name="Calendar sync"
        logo={
          <div className="flex flex-row items-center justify-center gap-x-3">
            <GoogleCalendarLogo className="h-16 w-16" />
            <MicrosoftOutlookLogo className="h-16 w-16" />
          </div>
        }
        isConnected={Boolean(authedProviderUser?.hasNylasIntegration)}
        isLoading={isIntegrationLoading}
        CustomConnectButton={
          // Show connect buttons when disconnected
          !Boolean(authedProviderUser?.hasNylasIntegration) && (
            <div className="flex flex-col gap-1">
              <IconButton
                IconComponent={SignInWithGoogle}
                aria-label="Sign in with Google"
                className="w-full focus:rounded-full"
                hoverClassName="hover:bg-transparent"
                onClick={onPressConnectGoogle}
                disabled={isIntegrationLoading}
              />
              <IconButton
                IconComponent={SignInWithMicrosoft}
                aria-label="Sign in with Microsoft"
                className="w-full focus:rounded-full"
                hoverClassName="hover:bg-transparent"
                onClick={onPressConnectMicrosoft}
                disabled={isIntegrationLoading}
              />
            </div>
          )
        }
        accountInfo={
          Boolean(authedProviderUser?.hasNylasIntegration) ? (
            <div className="flex flex-row items-center justify-center gap-x-2 text-caption text-neutral-125">
              {isMicrosoftProvider(
                authedProviderUser?.nylasCalendarProvider,
              ) ? (
                <MicrosoftOutlookLogo className="h-4 w-4 flex-shrink-0" />
              ) : (
                <GoogleCalLogo className="h-4 w-4 flex-shrink-0" />
              )}
              <span>Connected</span>
            </div>
          ) : undefined
        }
        onPressConnect={onPressConnectGoogle}
        onPressDisconnect={onPressDisconnectNylas}
        successMessage={successMessage}
        dismissSuccessMessage={() => setSuccessMessage(null)}
        errorMessage={errorMessage}
        dismissErrorMessage={() => setErrorMessage(null)}
        pricing="Free"
        extraSupportSections={[
          {
            title: 'Supported calendars',
            content: (
              <div className="flex flex-col gap-2">
                <div className="flex flex-row items-center gap-x-2">
                  <GoogleCalLogo className="h-4 w-4 flex-shrink-0" />
                  <span>Google Calendar</span>
                </div>
                <div className="flex flex-row items-center gap-x-2">
                  <MicrosoftOutlookLogo className="h-4 w-4 flex-shrink-0" />
                  <span>Microsoft Outlook</span>
                </div>
              </div>
            ),
          },
        ]}
        description={
          <>
            Easily sync your Google Calendar or Microsoft Outlook with
            Homecoming to view all your appointments and availability.
          </>
        }
        features={[
          {
            title: 'Organize your workflow with all your events in one place',
            description:
              'With real-time calendar sync, your calendar stays organized and always up to date, without the need for juggling multiple calendars.',
          },
          {
            title: 'Real-time updates and avoid scheduling conflicts.',
            description:
              'Any changes made to your calendar are instantly reflected, preventing double bookings and ensuring both you and your clients stay in sync with accurate availability.',
          },
        ]}
        disconnectConfirmText="Your calendar events will no longer be synced to Homecoming, and you will no longer be able to track session data through Homecoming."
      ></Integration>
    </PageContainer>
  );
};

export default NylasIntegration;
